// MorrowOS org member directory — client helpers for the DB-backed rich member
// records that sit alongside Clerk's invite/membership lifecycle.
//
//   GET   /api/morrow/org/members          -> { members: OrgMember[] }
//   POST  /api/morrow/org/members          -> { ok, member }
//   PATCH /api/morrow/org/members/:email   -> { ok, member }
//
// The org id is derived server-side from the caller's VERIFIED Clerk token, so
// no id is sent from here; the global fetch wrapper (installApiFetchAuth) attaches
// `x-clerk-token`. Every call is non-throwing — a failure resolves to null so the
// team UI keeps rendering. These endpoints require an active org + the
// `org:sys_memberships:manage` permission; a non-admin gets 403.
import { apiUrl } from "./apiBase";
import { getClerkToken, getOwnerId } from "./clerkTokenBridge";

/** Auth headers (Clerk token carries the verified org) for org-scoped requests. */
async function orgReqHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { accept: "application/json", ...extra };
  const token = await getClerkToken().catch(() => null);
  if (token) headers["x-clerk-token"] = token;
  const ownerId = getOwnerId();
  if (ownerId) headers["x-morrow-owner-id"] = ownerId;
  return headers;
}

/**
 * Cap the org's Clerk seats at the plan limit (best-effort). Once synced, Clerk
 * enforces seats in every surface — our Team page AND Clerk's own Members UI.
 * Safe to call on load; the server is admin-gated and a no-op for non-admins.
 */
export async function syncOrgSeats(): Promise<void> {
  try {
    await fetch(apiUrl("/api/morrow/org/sync-seats"), {
      method: "POST",
      headers: await orgReqHeaders(),
    });
  } catch {
    // best-effort
  }
}

export type OrgClerkRole = "org:admin" | "org:member";

/**
 * The four per-member access grants an admin can toggle. Each is additive over
 * the member's Clerk role/permission: turning one on gives that member the
 * matching capability without changing their Clerk role.
 */
export interface MemberGrants {
  readonly vaultManage: boolean;
  readonly vaultRequest: boolean;
  readonly billingAccess: boolean;
  readonly orgKbAccess: boolean;
}

export const NO_MEMBER_GRANTS: MemberGrants = {
  vaultManage: false,
  vaultRequest: false,
  billingAccess: false,
  orgKbAccess: false,
};

export interface OrgMember {
  readonly email: string;
  /** Human display name (from Clerk's membership when merged); may be empty. */
  readonly name?: string | undefined;
  readonly userId: string | null;
  readonly clerkRole: string;
  readonly title: string;
  readonly functionRole: string;
  readonly description: string;
  readonly avatarSeed: string;
  readonly defaultSensitivity: string;
  readonly invitedBy: string | null;
  readonly joined: boolean;
  readonly grants: MemberGrants;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface OrgMemberInput {
  readonly email: string;
  readonly role: OrgClerkRole;
  readonly title: string;
  readonly functionRole: string;
  readonly description: string;
  readonly defaultSensitivity: string;
  /**
   * Where the Clerk invitation email link should land. Defaults to this app's
   * own origin so the invitee opens Morrow (which consumes `__clerk_ticket`)
   * instead of Clerk's hosted portal. The backend validates it against an
   * allowlist before forwarding it to Clerk.
   */
  readonly redirectUrl?: string;
}

const str = (value: unknown): string => (typeof value === "string" ? value : "");
const nstr = (value: unknown): string | null => (typeof value === "string" ? value : null);

function parseGrants(value: unknown): MemberGrants {
  if (typeof value !== "object" || value === null) return NO_MEMBER_GRANTS;
  const rec = value as Record<string, unknown>;
  return {
    vaultManage: rec.vaultManage === true,
    vaultRequest: rec.vaultRequest === true,
    billingAccess: rec.billingAccess === true,
    orgKbAccess: rec.orgKbAccess === true,
  };
}

function parseMember(value: unknown): OrgMember | null {
  if (typeof value !== "object" || value === null) return null;
  const rec = value as Record<string, unknown>;
  const email = str(rec.email).trim();
  if (!email) return null;
  return {
    email,
    name: str(rec.name),
    userId: nstr(rec.userId),
    clerkRole: str(rec.clerkRole) || "org:member",
    title: str(rec.title),
    functionRole: str(rec.functionRole),
    description: str(rec.description),
    avatarSeed: str(rec.avatarSeed ?? rec.avatarCharacterId),
    defaultSensitivity: str(rec.defaultSensitivity) || "normal",
    invitedBy: nstr(rec.invitedBy),
    joined: rec.joined === true,
    grants: parseGrants(rec.grants),
    createdAt: str(rec.createdAt),
    updatedAt: str(rec.updatedAt),
  };
}

/** GET the caller's active-org members, or null on failure / no permission. */
export async function fetchOrgMembers(): Promise<OrgMember[] | null> {
  try {
    const res = await fetch(apiUrl("/api/morrow/org/members"), {
      headers: await orgReqHeaders(),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    if (!Array.isArray(data.members)) return [];
    return data.members.flatMap((entry) => {
      const parsed = parseMember(entry);
      return parsed ? [parsed] : [];
    });
  } catch {
    return null;
  }
}

/**
 * POST a member being invited/added. The backend persists the rich domain fields
 * AND sends the authoritative Clerk invitation (with a `redirectUrl` so the email
 * opens this app, not Clerk's hosted portal). Throws a `{ status: 402 }` error on
 * a seat-limit response so callers can show the upgrade prompt (see
 * {@link import("./components/workspace/MorrowTeamPage").isSeatLimitError}); other
 * failures resolve to `null`.
 */
export async function createOrgMember(input: OrgMemberInput): Promise<OrgMember | null> {
  let res: Response;
  try {
    res = await fetch(apiUrl("/api/morrow/org/members"), {
      method: "POST",
      headers: await orgReqHeaders({ "content-type": "application/json" }),
      body: JSON.stringify({
        email: input.email,
        clerk_role: input.role,
        title: input.title,
        function_role: input.functionRole,
        description: input.description,
        default_sensitivity: input.defaultSensitivity,
        redirectUrl: input.redirectUrl ?? window.location.origin,
      }),
    });
  } catch {
    return null;
  }
  if (res.status === 402) {
    const seatErr = new Error("seat limit reached") as Error & { status?: number };
    seatErr.status = 402;
    throw seatErr;
  }
  if (!res.ok) return null;
  try {
    const data = (await res.json()) as Record<string, unknown>;
    return parseMember(data.member);
  } catch {
    return null;
  }
}

/** PATCH a member's rich fields (admin edit). Only supplied fields change. */
export async function updateOrgMember(
  email: string,
  patch: Partial<Omit<OrgMemberInput, "email">> & {
    readonly grants?: Partial<MemberGrants>;
    readonly avatarSeed?: string;
  },
): Promise<OrgMember | null> {
  try {
    const body: Record<string, unknown> = {};
    if (patch.role !== undefined) body.clerk_role = patch.role;
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.functionRole !== undefined) body.function_role = patch.functionRole;
    if (patch.description !== undefined) body.description = patch.description;
    if (patch.defaultSensitivity !== undefined) body.default_sensitivity = patch.defaultSensitivity;
    if (patch.avatarSeed !== undefined) body.avatar_seed = patch.avatarSeed;
    // Grant flags map to the server's snake_case columns; only send the ones set.
    const grants = patch.grants;
    if (grants) {
      if (grants.vaultManage !== undefined) body.grant_vault_manage = grants.vaultManage;
      if (grants.vaultRequest !== undefined) body.grant_vault_request = grants.vaultRequest;
      if (grants.billingAccess !== undefined) body.grant_billing_access = grants.billingAccess;
      if (grants.orgKbAccess !== undefined) body.grant_org_kb_access = grants.orgKbAccess;
    }
    const res = await fetch(apiUrl(`/api/morrow/org/members/${encodeURIComponent(email)}`), {
      method: "PATCH",
      headers: await orgReqHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    return parseMember(data.member);
  } catch {
    return null;
  }
}

/** PATCH the caller's rich profile fields in the active org. */
export async function updateMyOrgMemberProfile(patch: {
  readonly avatarSeed?: string;
  readonly title?: string;
  readonly functionRole?: string;
}): Promise<OrgMember | null> {
  try {
    const body: Record<string, unknown> = {};
    if (patch.avatarSeed !== undefined) body.avatar_seed = patch.avatarSeed;
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.functionRole !== undefined) body.function_role = patch.functionRole;
    if (Object.keys(body).length === 0) return null;
    const res = await fetch(apiUrl("/api/morrow/org/members/me"), {
      method: "PATCH",
      headers: await orgReqHeaders({ "content-type": "application/json" }),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Record<string, unknown>;
    return parseMember(data.member);
  } catch {
    return null;
  }
}

/** @deprecated Use {@link updateMyOrgMemberProfile}. */
export async function updateMyOrgMemberAvatar(avatarSeed: string): Promise<OrgMember | null> {
  return updateMyOrgMemberProfile({ avatarSeed });
}
